import Page from 'components/Page';
import React, { useEffect, useState } from 'react';
import { styled } from '@mui/system';
import Tab from 'components/Tab';
import {
  Box,
  Input,
  Typography,
  InputAdornment,
  Stack,
  Button,
} from '@mui/material';
import GetAppIcon from '@mui/icons-material/GetApp';
import { Table, Th, Tr, Td, TdTable } from 'components/Table';
import { useNavigate } from 'react-router-dom';
import CashModal from 'components/CashModal';
import { API_PATH } from 'common/constants/services';
import useQuery from 'hook/useQuery';
import ListResponse from 'model/ListResponse';
import useCoachList from 'hook/useCoachList';
import { Settlement, SETTLEMENTS_STATUS } from 'model/Settlements';
import useSettlementsUser from 'hook/useSettlementsUser';
import QueryString from 'qs';
import dayjs from 'dayjs';
import commaNumber from 'comma-number';
import MonthModal from 'components/MonthModal';
import { exportToCSV } from 'utils/csvToJson';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import TextField from '@mui/material/TextField';
import { RootStyle } from './style';

const TAB_LIST = [
  { name: '전체', value: '' },
  { name: '판매 중', value: 'SELL' },
  { name: '정산 예정', value: 'WAIT' },
  { name: '정산 완료', value: 'DONE' },
];
const TabRow = styled('div')`
  display: flex;
  justify-content: space-between;
`;

export default function Cash() {
  const [activeTab, setActiveTab] = useState(0);
  const navigate = useNavigate();
  const [isOpenCashModal, setIsOpenCashModal] = useState(false);
  const [activeCoach, setActiveCoach] = useState<undefined | number>(undefined);
  const [activeDate, setActiveDate] = useState({
    year: 0,
    month: 0,
  });
  const [filterDate, setFilterDate] = useState<Date>(new Date());
  const coachList = useCoachList();
  const { data } = useSettlementsUser(
    TAB_LIST[activeTab].value,
    dayjs(filterDate).year(),
    dayjs(filterDate).month() + 1,
  );
  useEffect(() => {
    if (!isOpenCashModal) setActiveCoach(undefined);
  }, [isOpenCashModal]);
  const [isMonthModal, setIsMonthModal] = useState(false);
  const handleExportCSV = () => {
    // CSV로 내보낼 데이터 준비
    const csvData = [];

    // 헤더 추가
    csvData.push(['월', '이름', '정산액']);

    // 데이터 추가
    Object.keys(data || {}).forEach((yearMonth) => {
      Object.keys(data[yearMonth]).forEach((coachName) => {
        const totalAmount = data[yearMonth][coachName].reduce(
          (acc, settlement) => acc + settlement.net_amount,
          0,
        );

        // 월 데이터에서 YYYY-MM 형식을 MM월로 변환
        const month = `${yearMonth.split('-')[1]}월`;

        csvData.push([month, coachName, totalAmount]);
      });
    });

    // CSV 파일 내보내기
    exportToCSV(csvData, `정산내역_${new Date().toISOString().split('T')[0]}`);
  };

  return (
    <Page
      actions={
        <Stack direction='row' spacing={2}>
          <Button
            variant='contained'
            startIcon={<GetAppIcon />}
            onClick={handleExportCSV}
          >
            CSV 다운로드
          </Button>
          <Button variant='contained' onClick={() => setIsMonthModal(true)}>
            월별 정산하기
          </Button>
        </Stack>
      }
    >
      <Stack direction='row' justifyContent='space-between' mb={5}>
        <Stack direction='row'>
          {TAB_LIST.map((tab, i) => {
            return (
              <Box mr={1}>
                <Tab
                  label={tab.name}
                  active={activeTab === i}
                  onClick={() => setActiveTab(i)}
                />
              </Box>
            );
          })}
        </Stack>
        <Box>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              views={['year', 'month']}
              label='기준 월'
              minDate={new Date('2012-03-01')}
              maxDate={new Date('2050-06-01')}
              value={filterDate}
              onChange={(newValue) => {
                if (newValue) setFilterDate(newValue as Date);
              }}
              renderInput={(params) => (
                <TextField {...params} size='small' helperText={null} />
              )}
            />
          </LocalizationProvider>
        </Box>
      </Stack>
      <Table>
        <Tr>
          <Th width='200px'>기준월</Th>
          <Th width='200px'>코치이름</Th>
          <Th>루틴 판매내역</Th>
          <Th>사용자 수</Th>
          <Th width='200px'>정산금액</Th>
          <Th width='140px'>정산하기</Th>
        </Tr>
        {Object.keys(data || {}).map((yearMonth) => {
          const [year, month] = [
            dayjs(yearMonth, 'YYYY-MM').year(),
            dayjs(yearMonth, 'YYYY-MM').month() + 1,
          ];

          return (
            <Tr>
              <Td width='200px'>{yearMonth}</Td>
              <Td>
                <TdTable>
                  {Object.keys(data[yearMonth]).map((coachName) => {
                    function SubmitButton() {
                      switch (data[yearMonth][coachName][0].status) {
                        case SETTLEMENTS_STATUS.DONE:
                          return (
                            <Button
                              variant='contained'
                              disableElevation
                              fullWidth
                              onClick={() => {
                                setActiveCoach(
                                  data[yearMonth][coachName][0].coach_user.id,
                                );
                                setActiveDate({ year, month });
                                setIsOpenCashModal(true);
                              }}
                              color='inherit'
                            >
                              수정하기
                            </Button>
                          );

                        case SETTLEMENTS_STATUS.SELLING:
                          return <Typography>판매 중</Typography>;
                        case SETTLEMENTS_STATUS.WAITING:
                          return (
                            <Button
                              variant='contained'
                              disableElevation
                              fullWidth
                              onClick={() => {
                                setActiveCoach(
                                  data[yearMonth][coachName][0].coach_user.id,
                                );
                                setActiveDate({ year, month });
                                setIsOpenCashModal(true);
                              }}
                            >
                              정산하기
                            </Button>
                          );
                        default:
                          return <Typography>정보 없음</Typography>;
                      }
                    }
                    return (
                      <Tr>
                        <Td width='200px' style={{ cursor: 'pointer' }}>
                          <Typography>{coachName}</Typography>
                        </Td>
                        <Td style={{ cursor: 'pointer' }}>
                          <TdTable>
                            {data[yearMonth][coachName].map((settlement) => {
                              return (
                                <Tr>
                                  <Td style={{ cursor: 'pointer' }}>
                                    <Typography
                                      fontWeight={700}
                                      onClick={() =>
                                        navigate(
                                          `/cash/challenge/${
                                            settlement.challenge.id
                                          }${QueryString.stringify(
                                            {
                                              year,
                                              month,
                                              coachId: settlement.coach_user.id,
                                            },
                                            { addQueryPrefix: true },
                                          )}`,
                                        )
                                      }
                                    >
                                      {settlement.challenge.name}
                                    </Typography>
                                  </Td>
                                  <Td style={{ cursor: 'pointer' }}>
                                    <Typography
                                      fontWeight={700}
                                      onClick={() =>
                                        navigate(
                                          `/cash/challenge/${
                                            settlement.challenge.id
                                          }${QueryString.stringify(
                                            {
                                              year,
                                              month,
                                              coachId: settlement.coach_user.id,
                                            },
                                            { addQueryPrefix: true },
                                          )}`,
                                        )
                                      }
                                    >
                                      {settlement.user_count}
                                    </Typography>
                                  </Td>
                                </Tr>
                              );
                            })}
                          </TdTable>
                        </Td>
                        <Td
                          width='200px'
                          onClick={() => {
                            // 클립보드에 금액 복사
                            const message = `${dayjs(
                              yearMonth,
                              'YYYY-MM',
                            ).format('YYYY년 M월')} 정산내역 공유드립니다!
${commaNumber(
  data[yearMonth][coachName].reduce(
    (acc, settlement) => acc + settlement.net_amount,
    0,
  ),
)}원 입니다.
잘부탁드립니다. 감사합니다!`;
                            navigator.clipboard.writeText(message);
                          }}
                        >
                          {commaNumber(
                            data[yearMonth][coachName].reduce(
                              (acc, settlement) => acc + settlement.net_amount,
                              0,
                            ),
                          )}
                          원
                        </Td>
                        <Td width='140px'>
                          <SubmitButton />
                        </Td>
                      </Tr>
                    );
                  })}
                </TdTable>
              </Td>
            </Tr>
          );
        })}
      </Table>
      {!!activeCoach && (
        <CashModal
          isOpen={isOpenCashModal}
          close={() => setIsOpenCashModal(false)}
          userId={activeCoach || 0}
          year={activeDate.year}
          month={activeDate.month}
        />
      )}
      <MonthModal isOpen={isMonthModal} close={() => setIsMonthModal(false)} />
    </Page>
  );
}
